import { PlusOutlined } from "@ant-design/icons";
import { Col, Row, Switch } from "antd";
import cuid from "cuid";
import Processes from "entities/Processes";
import { FieldArray, Formik } from "formik";
import { find, get, isEmpty } from "lodash";
import { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiCheck, BiChevronLeft, BiInfoCircle, BiTrash } from "react-icons/bi";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import DatePicker from "shared/components/Form/DatePicker";
import Header from "shared/components/Header";
import InsurancePlanIdccRuleSelect from "shared/components/InsurancePlanIdccRuleSelect";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import { PRODUCT_RANGES } from "utils/api/graphql/queries/product-range-advisor-codes";
import { COLORS } from "shared/style/colors";
import {
  ATTACHMENT_TYPES,
  CONTACT_EXERCISE_FRAME,
  DELTA_END_TYPES,
  DEPARTMENTS,
  GENERATED_DOCUMENTS_TYPES,
  GENERATED_DOCUMENTS_VERSIONS,
  INSURANCE_PLAN_PARAMS_RULES,
  INSURANCE_PLAN_RISKS,
  JOB_LIST,
  LMF_ASSOCIATION_MEMBER_TYPE,
  PAYMENT_FREQUENCIES,
  PAYMENT_TYPES,
  PERIOD_UNITS,
  PROFILE_LIST,
  SOCIAL_REGIMES,
  SUPPORTING_INFORMATION_CATEGORIES,
} from "utils/constants";
import { bindArrayInputProps, bindInputProps } from "utils/helpers/input";
import validation from "./validation";
import Tooltip from "shared/components/Tooltip";
import { COMMISSIONS } from "utils/api/graphql/queries/commissions";
import RuleMultiSelectWithExclude from "../../shared/RuleMultiSelectWithExclude";
import { applyExcludeTransformToParams } from "../../shared/subscriptionRulesExclude";

const fieldHintSuffix = (hintText) =>
  hintText ? (
    <Tooltip title={hintText}>
      <BiInfoCircle size={14} className="c-gray" />
    </Tooltip>
  ) : null;

const Create = ({ onSubmit, cancel, risk }) => {
  const [excludeFlags, setExcludeFlags] = useState({});
  const dynamicAllValuesRef = useRef({});

  const handleSubmitWithExclude = (values) =>
    onSubmit({
      ...values,
      params: applyExcludeTransformToParams(
        values.params,
        excludeFlags,
        dynamicAllValuesRef.current,
      ),
    });
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const CREATE = t("CREATE", { returnObjects: true });
  const FORM = t("FORM", { returnObjects: true });
  const { user: { organization: userOrganization } = {} } =
    useContext(StoreContext);
  const organizationId = get(userOrganization, "id");
  const { data: productRangesData } = useQuery(PRODUCT_RANGES, {
    variables: {
      where: { ownedBy: { id: organizationId } },
      take: 500,
      orderBy: [{ code: "ASC" }],
    },
    skip: !organizationId,
  });
  const handleFileUpload = async (file, setFieldValue) => {
    const { base64, size, uid, ...fileData } = file;
    setFieldValue("iconAttachment", {
      ...fileData,
      id: cuid(),
      content: base64,
      type: ATTACHMENT_TYPES.ICON,
    });
    setFieldValue("icon", file.base64);
  };
  return (
    <Formik
      initialValues={{
        commissions: [],
        params: [],
        supportingInformation: [],
      }}
      onSubmit={handleSubmitWithExclude}
      validationSchema={validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      )}
    >
      {({
        isValid,
        handleSubmit,
        setFieldValue,
        setValues,
        values,
        ...formProps
      }) => [
        <Header
          key={"header"}
          title={CREATE.HEADER.TITLE}
          subtitle={CREATE.HEADER.ACTION}
          actions={[
            {
              text: CREATE.HEADER.DISCARD,
              onClick: cancel,
              type: "primary--outlined",
              icon: <BiChevronLeft size={20} />,
            },
            {
              text: CREATE.HEADER.SAVE,
              onClick: handleSubmit,
              type: "primary",
              icon: <BiCheck size={20} />,
            },
          ]}
        />,
        <Card key={"generalInfo"} divider={false} title={FORM.GENERAL_INFO}>
          <div className="grid--1">
            <div className="grid--3">
              <div className="d-flex">
                <span>{FORM.ACTIVE}</span>
                <Switch
                  className="button__switch"
                  checkedChildren={FORM.YES}
                  unCheckedChildren={FORM.NO}
                  defaultChecked={false}
                  onChange={(e, _) => setFieldValue("active", e)}
                />
              </div>
              <div className="d-flex">
                <span>{FORM.INTERNAL}</span>
                <Switch
                  className="button__switch"
                  checkedChildren={FORM.YES}
                  unCheckedChildren={FORM.NO}
                  defaultChecked={get(values, "internal")}
                  onChange={(e, _) => setFieldValue("internal", e)}
                />
              </div>
              {risk !== INSURANCE_PLAN_RISKS.PROVIDENT && (
                <div className="d-flex">
                  <span>{FORM.MADELIN}</span>
                  <Switch
                    className="button__switch"
                    checkedChildren={FORM.YES}
                    unCheckedChildren={FORM.NO}
                    defaultChecked={false}
                    onChange={(e, _) => setFieldValue("fields.madelin", e)}
                  />
                </div>
              )}
            </div>
            <div className="grid--2">
              <Input
                placeholder={FORM.NAME}
                label={FORM.NAME}
                {...bindInputProps({ name: "name", values, ...formProps })}
              />

              <Input
                placeholder={FORM.DESCRIPTION}
                label={FORM.DESCRIPTION}
                {...bindInputProps({
                  name: "description",
                  values,
                  ...formProps,
                })}
              />
              <Input
                placeholder={FORM.ALIAS}
                label={FORM.ALIAS}
                {...bindInputProps({ name: "alias", values, ...formProps })}
              />
              {(() => {
                const rows = get(productRangesData, "productRanges.data", []);
                const opts = rows.map((r) => ({
                  label: r.label ? `${r.code} — ${r.label}` : r.code,
                  value: r.code,
                }));
                const currentProduct = get(values, "product");
                const productOptions =
                  currentProduct &&
                  !opts.some((o) => o.value === currentProduct)
                    ? [
                        { label: currentProduct, value: currentProduct },
                        ...opts,
                      ]
                    : opts;
                return (
                  <>
                    <Select
                      labelSuffix={fieldHintSuffix(FORM.PRODUCT_RANGE_HELP)}
                      label={FORM.PRODUCT_RANGE || FORM.PRODUCT}
                      placeholder={FORM.PRODUCT_RANGE_PLACEHOLDER}
                      options={productOptions}
                      value={currentProduct || undefined}
                      onChange={(next) =>
                        setFieldValue("product", next == null ? "" : next)
                      }
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      disabled={!organizationId}
                    />
                    <p className="mb-0">{FORM.PRODUCT_RANGE_HELP}</p>
                  </>
                );
              })()}
              <Input
                placeholder={FORM.IDENTIFIER}
                label={FORM.IDENTIFIER}
                {...bindInputProps({
                  name: "identifier",
                  values,
                  ...formProps,
                })}
              />
              <Input
                placeholder={FORM.EXTERNAL_ID}
                label={FORM.EXTERNAL_ID}
                {...bindInputProps({
                  name: "externalId",
                  values,
                  ...formProps,
                })}
              />
              <Input
                type="number"
                placeholder={FORM.LEVEL}
                label={FORM.LEVEL}
                {...bindInputProps({ name: "level", values, ...formProps })}
              />
              <Input
                placeholder={FORM.CATEGORY}
                label={FORM.CATEGORY}
                {...bindInputProps({ name: "category", values, ...formProps })}
              />
              <DatePicker
                placeholder={FORM.DATE}
                label={FORM.DATE}
                placeholders={[FORM.START_DATE, FORM.END_DATE]}
                onChange={(_, dates) => setFieldValue("date", dates)}
              />
              <Input
                type="file"
                variant="uploader"
                listType="picture"
                showUploadList={false}
                maxCount={1}
                onUpload={([file]) => handleFileUpload(file, setFieldValue)}
                fileList={
                  isEmpty(get(values, "icon", [])) ? [] : [get(values, "icon")]
                }
              >
                <Button type="primary--link">{FORM.ICON}</Button>
                <div>
                  {get(values, "icon") && (
                    <img
                      className="icon"
                      alt="icon"
                      src={get(values, "icon")}
                    />
                  )}
                </div>
              </Input>
            </div>
          </div>
        </Card>,
        <div className="m-bottom-24" key="divider-1" />,
        <Card title={FORM.COMMISSIONS.TITLE} divider={false} key="commission">
          <div className="grid--4">
            <Select
              type="async"
              mode="multiple"
              node={{
                query: COMMISSIONS,
                accessors: {
                  root: "commissions.data",
                  label: {
                    separator: "/",
                    keys: ["value", "withholding/value"],
                  },
                },
              }}
              placeholder={FORM.COMMISSIONS.TITLE}
              label={FORM.COMMISSIONS.TITLE}
              {...bindInputProps({
                name: "commissions",
                values,
                ...formProps,
              })}
              onChange={(value) => setFieldValue("commissions", value)}
            />
          </div>
        </Card>,
        <div className="m-bottom-24" key="divider-2" />,
        <Card
          divider={false}
          title={FORM.CONDITIONS.PAYMENT_TITLE}
          key="conditions"
        >
          <Col span={24}>
            <Row gutter={[16, 16]} align={"middle"}>
              <Col span={24}>
                <div className="grid--3 m-bottom-16">
                  <Input
                    type="checkbox"
                    label={FORM.TNS}
                    {...bindInputProps({ name: "fields.tns", ...formProps })}
                    checked={!!get(values, "fields.tns")}
                  />
                  <Input
                    type="checkbox"
                    label={FORM.CONDITIONS.ALLOWED_PAYER}
                    {...bindInputProps({
                      name: "fields.payment.shouldBeAdherent",
                      ...formProps,
                    })}
                    checked={!!get(values, "fields.payment.shouldBeAdherent")}
                  />
                </div>
              </Col>
              <Col span={8}>
                <Select
                  mode="multiple"
                  options={Object.entries(PAYMENT_TYPES).map(
                    ([key, value]) => ({
                      value,
                      label: FORM.CONDITIONS.PAYMENT_TYPE.OPTIONS[key],
                    }),
                  )}
                  placeholder={FORM.CONDITIONS.PAYMENT_TYPE.LABEL}
                  label={FORM.CONDITIONS.PAYMENT_TYPE.LABEL}
                  {...bindInputProps({
                    name: "fields.payment.type",
                    values,
                    ...formProps,
                  })}
                  onChange={(value) =>
                    setFieldValue(`fields.payment.type`, value)
                  }
                />
              </Col>
              <Col span={8}>
                <Select
                  mode="multiple"
                  options={Object.entries(PAYMENT_FREQUENCIES).map(
                    ([key, value]) => ({
                      value,
                      label: FORM.CONDITIONS.PAYMENT_FREQUENCY.OPTIONS[key],
                    }),
                  )}
                  placeholder={FORM.CONDITIONS.PAYMENT_FREQUENCY.LABEL}
                  label={FORM.CONDITIONS.PAYMENT_FREQUENCY.LABEL}
                  {...bindInputProps({
                    name: "fields.payment.frequency",
                    values,
                    ...formProps,
                  })}
                  onChange={(value) =>
                    setFieldValue(`fields.payment.frequency`, value)
                  }
                />
              </Col>
              <Col span={8}>
                <Input
                  type="checkbox"
                  label={FORM.CONDITIONS.ALLOWED_PAYER}
                  {...bindInputProps({
                    name: "fields.payment.shouldBeAdherent",
                    ...formProps,
                  })}
                  checked={!!get(values, "fields.payment.shouldBeAdherent")}
                />
              </Col>
            </Row>
          </Col>
        </Card>,
        <div key="margin-3" className="m-bottom-16" />,
        <Card divider={false} title={FORM.CONDITIONS.TITLE} key="rules">
          <FieldArray
            name={"params"}
            render={(arrayHelpers) => [
              get(values, "params", []).map((_, i) => [
                <Col span={24} key="conditions">
                  <div key="params" className="grid--1">
                    <div
                      className={"d-flex justify--between m-top-8 delete-icon"}
                    >
                      <h3 className="card--title">{`${
                        FORM.CONDITIONS.PARAMETERS
                      } ${i + 1}`}</h3>
                      <BiTrash
                        color={COLORS.C_DANGER}
                        size={24}
                        onClick={() => {
                          arrayHelpers.remove(i);
                        }}
                      />
                    </div>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.ISSUANCE_DATE}</Col>
                      <Col sm={24} md={10}>
                        <Row align={"middle"} gutter={8}>
                          <Col>{FORM.CONDITIONS.TODAY + " +"}</Col>
                          <Col flex="1">
                            <Input
                              type="number"
                              placeholder={FORM.CONDITIONS.START_DATE}
                              {...bindArrayInputProps({
                                parent: "params",
                                index: i,
                                name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_START}.value`,
                                values,
                                ...formProps,
                              })}
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col sm={24} md={10}>
                        <Row align={"middle"} gutter={8}>
                          <Col flex={1}>
                            <Select
                              options={Object.entries(DELTA_END_TYPES).map(
                                ([key, value]) => ({
                                  label:
                                    FORM.CONDITIONS.MAX_ISSUANCE_DATE_OPTIONS[
                                      key
                                    ],
                                  value,
                                }),
                              )}
                              allowClear
                              placeholder={FORM.CONDITIONS.END_DATE}
                              {...bindArrayInputProps({
                                parent: "params",
                                index: i,
                                name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.type`,
                                values,
                                ...formProps,
                              })}
                              onChange={(value) => {
                                value
                                  ? setFieldValue(
                                      `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.type`,
                                      value,
                                    )
                                  : setFieldValue(
                                      `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}`,
                                      null,
                                    );
                              }}
                            />
                          </Col>
                          {get(
                            values,
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.type`,
                          ) === DELTA_END_TYPES.ADD_PERIOD && (
                            <>
                              <Col span={9}>
                                <Input
                                  type="number"
                                  placeholder={FORM.CONDITIONS.NUMBER_OF}
                                  {...bindArrayInputProps({
                                    parent: "params",
                                    index: i,
                                    name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.value`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </Col>
                              <Col span={5}>
                                <Select
                                  options={Object.entries(PERIOD_UNITS).map(
                                    ([key, value]) => ({
                                      label: FORM.CONDITIONS[key],
                                      value,
                                    }),
                                  )}
                                  placeholder={FORM.CONDITIONS.UNIT}
                                  {...bindArrayInputProps({
                                    parent: "params",
                                    index: i,
                                    name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.unit`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </Col>
                            </>
                          )}
                        </Row>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.ISSUANCE_DATE_RIA}</Col>
                      <Col span={10}>
                        <Row align={"middle"} gutter={8}>
                          <Col>{FORM.CONDITIONS.TODAY + " +"}</Col>
                          <Col flex="1">
                            <Input
                              type="number"
                              placeholder={FORM.CONDITIONS.START_DATE_RIA}
                              {...bindArrayInputProps({
                                parent: "params",
                                index: i,
                                name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA}.value`,
                                values,
                                ...formProps,
                              })}
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col span={10}>
                        <Row align={"middle"} gutter={8}>
                          <Col flex={1}>
                            <Select
                              options={Object.entries(DELTA_END_TYPES).map(
                                ([key, value]) => ({
                                  label:
                                    FORM.CONDITIONS.MAX_ISSUANCE_DATE_OPTIONS[
                                      key
                                    ],
                                  value,
                                }),
                              )}
                              placeholder={FORM.CONDITIONS.END_DATE_RIA}
                              {...bindArrayInputProps({
                                parent: "params",
                                index: i,
                                name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.type`,
                                values,
                                ...formProps,
                              })}
                              allowClear
                              onChange={(value) => {
                                value
                                  ? setFieldValue(
                                      `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.type`,
                                      value,
                                    )
                                  : setFieldValue(
                                      `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}`,
                                      null,
                                    );
                              }}
                            />
                          </Col>
                          {get(
                            values,
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.type`,
                          ) === DELTA_END_TYPES.ADD_PERIOD && (
                            <>
                              <Col span={9}>
                                <Input
                                  type="number"
                                  placeholder={FORM.CONDITIONS.NUMBER_OF}
                                  {...bindArrayInputProps({
                                    parent: "params",
                                    index: i,
                                    name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.value`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </Col>
                              <Col span={5}>
                                <Select
                                  options={Object.entries(PERIOD_UNITS).map(
                                    ([key, value]) => ({
                                      label: FORM.CONDITIONS[key],
                                      value,
                                    }),
                                  )}
                                  placeholder={FORM.CONDITIONS.UNIT}
                                  {...bindArrayInputProps({
                                    parent: "params",
                                    index: i,
                                    name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.unit`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </Col>
                            </>
                          )}
                        </Row>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.QUOTE_VALIDITY}</Col>
                      <Col span={20}>
                        <Row align={"middle"} gutter={8}>
                          <Col flex={1}>
                            <Select
                              options={Object.entries(DELTA_END_TYPES).map(
                                ([key, value]) => ({
                                  label:
                                    FORM.CONDITIONS.QUOTE_VALIDITY_OPTIONS[key],
                                  value,
                                }),
                              )}
                              allowClear
                              placeholder={FORM.CONDITIONS.QUOTE_VALIDITY}
                              {...bindArrayInputProps({
                                parent: "params",
                                index: i,
                                name: `rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.type`,
                                values,
                                ...formProps,
                              })}
                              onChange={(value) => {
                                value
                                  ? setFieldValue(
                                      `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.type`,
                                      value,
                                    )
                                  : setFieldValue(
                                      `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}`,
                                      null,
                                    );
                              }}
                            />
                          </Col>
                          {get(
                            values,
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.type`,
                          ) === DELTA_END_TYPES.ADD_PERIOD && (
                            <>
                              <Col span={9}>
                                <Input
                                  type="number"
                                  placeholder={FORM.CONDITIONS.NUMBER_OF}
                                  {...bindArrayInputProps({
                                    parent: "params",
                                    index: i,
                                    name: `rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.value`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </Col>
                              <Col span={5}>
                                <Select
                                  options={Object.entries(PERIOD_UNITS).map(
                                    ([key, value]) => ({
                                      label: FORM.CONDITIONS[key],
                                      value,
                                    }),
                                  )}
                                  placeholder={FORM.CONDITIONS.UNIT}
                                  {...bindArrayInputProps({
                                    parent: "params",
                                    index: i,
                                    name: `rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.unit`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </Col>
                            </>
                          )}
                        </Row>
                        {get(
                          values,
                          `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.type`,
                        ) === DELTA_END_TYPES.ADD_PERIOD && (
                          <Row
                            align={"middle"}
                            gutter={8}
                            className="m-top-8"
                          >
                            <Col>
                              {
                                FORM.CONDITIONS
                                  .QUOTE_VALIDITY_LIMIT_END_OF_YEAR
                              }
                            </Col>
                            <Col>
                              <Switch
                                checked={
                                  get(
                                    values,
                                    `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.limitEndOfYear`,
                                  ) === true
                                }
                                onChange={(checked) =>
                                  setFieldValue(
                                    `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.limitEndOfYear`,
                                    checked,
                                  )
                                }
                              />
                            </Col>
                          </Row>
                        )}
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>
                        {FORM.CONDITIONS.RETROACTIVITY_ENABLED}
                      </Col>
                      <Col span={20}>
                        <Switch
                          checked={
                            get(
                              values,
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.RETROACTIVITY_ENABLED}.value`,
                            ) === true ||
                            get(
                              values,
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.RETROACTIVITY_ENABLED}.value`,
                            ) === "true"
                          }
                          onChange={(checked) =>
                            setFieldValue(
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.RETROACTIVITY_ENABLED}.value`,
                              checked,
                            )
                          }
                        />
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.AGE}</Col>
                      <Col span={10}>
                        <Input
                          type="number"
                          placeholder={FORM.CONDITIONS.MIN}
                          {...bindArrayInputProps({
                            parent: "params",
                            index: i,
                            name: `rules.${INSURANCE_PLAN_PARAMS_RULES.MIN_AGE}.value`,
                            values,
                            ...formProps,
                          })}
                        />
                      </Col>
                      <Col sm={10}>
                        <Input
                          type="number"
                          placeholder={FORM.CONDITIONS.MAX}
                          {...bindArrayInputProps({
                            parent: "params",
                            index: i,
                            name: `rules.${INSURANCE_PLAN_PARAMS_RULES.MAX_AGE}.value`,
                            values,
                            ...formProps,
                          })}
                        />
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.SOCIAL_REGIMES}</Col>
                      <Col sm={20} lg={20}>
                        <RuleMultiSelectWithExclude
                          paramIndex={i}
                          ruleName={INSURANCE_PLAN_PARAMS_RULES.SOCIAL_REGIMES}
                          excludeLabel={FORM.CONDITIONS.EXCLUDED}
                          excludeFlags={excludeFlags}
                          setExcludeFlags={setExcludeFlags}
                          allOptionValues={Object.values(SOCIAL_REGIMES)}
                          allOptionValuesRef={dynamicAllValuesRef}
                          mode="multiple"
                          options={Object.entries(SOCIAL_REGIMES).map(
                            ([key, value]) => ({
                              value,
                              label:
                                FORM.CONDITIONS.SOCIAL_REGIMES_OPTIONS[key],
                            }),
                          )}
                          placeholder={FORM.CONDITIONS.SOCIAL_REGIMES}
                          {...bindArrayInputProps({
                            parent: "params",
                            index: i,
                            name: `rules.${INSURANCE_PLAN_PARAMS_RULES.SOCIAL_REGIMES}.value`,
                            values,
                            ...formProps,
                          })}
                          value={
                            get(
                              values,
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.SOCIAL_REGIMES}.value`,
                            ) || []
                          }
                          onChange={(value) =>
                            setFieldValue(
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.SOCIAL_REGIMES}.value`,
                              value,
                            )
                          }
                        />
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.EXERCISE_FRAME.LABEL}</Col>
                      <Col span={20}>
                        <Input
                          type="radio"
                          variant="group"
                          values={[
                            {
                              value: CONTACT_EXERCISE_FRAME.EMPLOYEE,
                              label:
                                FORM.CONDITIONS.EXERCISE_FRAME.OPTIONS.EMPLOYEE,
                            },
                            {
                              value: CONTACT_EXERCISE_FRAME.INDEPENDENT,
                              label:
                                FORM.CONDITIONS.EXERCISE_FRAME.OPTIONS
                                  .INDEPENDENT,
                            },
                            {
                              value: "",
                              label: FORM.CONDITIONS.EXERCISE_FRAME.OPTIONS.ANY,
                            },
                          ]}
                          {...bindArrayInputProps({
                            parent: "params",
                            index: i,
                            name: `rules.${INSURANCE_PLAN_PARAMS_RULES.EXERCISE_FRAME}.value`,
                            values,
                            ...formProps,
                          })}
                        />
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.PROFESSION.LABEL}</Col>
                      <Col sm={20} lg={20}>
                        <RuleMultiSelectWithExclude
                          paramIndex={i}
                          ruleName={INSURANCE_PLAN_PARAMS_RULES.PROFESSION}
                          excludeLabel={FORM.CONDITIONS.EXCLUDED}
                          excludeFlags={excludeFlags}
                          setExcludeFlags={setExcludeFlags}
                          allOptionValues={Object.values(JOB_LIST)}
                          allOptionValuesRef={dynamicAllValuesRef}
                          allowClear
                          mode="multiple"
                          options={Object.entries(JOB_LIST).map(
                            ([key, value]) => ({
                              label: FORM.CONDITIONS.PROFESSION.JOB_LIST[key],
                              value,
                            }),
                          )}
                          placeholder={FORM.CONDITIONS.PROFESSION.PLACEHOLDER}
                          {...bindArrayInputProps({
                            parent: "params",
                            index: i,
                            name: `rules.${INSURANCE_PLAN_PARAMS_RULES.PROFESSION}.value`,
                            values,
                            ...formProps,
                          })}
                          value={
                            get(
                              values,
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROFESSION}.value`,
                            ) || []
                          }
                          onChange={(value) =>
                            setFieldValue(
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROFESSION}.value`,
                              value,
                            )
                          }
                        />
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.PROFILE.LABEL}</Col>
                      <Col sm={20} lg={20}>
                        <RuleMultiSelectWithExclude
                          paramIndex={i}
                          ruleName={INSURANCE_PLAN_PARAMS_RULES.PROFILES}
                          excludeLabel={FORM.CONDITIONS.EXCLUDED}
                          excludeFlags={excludeFlags}
                          setExcludeFlags={setExcludeFlags}
                          allOptionValues={Object.values(PROFILE_LIST)}
                          allOptionValuesRef={dynamicAllValuesRef}
                          allowClear
                          mode="multiple"
                          options={Object.entries(PROFILE_LIST)
                            .map(([key, value]) => ({
                              label: FORM.CONDITIONS.PROFILE.OPTIONS[key],
                              value,
                            }))
                            .sort((a, b) =>
                              get(a, "label", "").localeCompare(b.label),
                            )}
                          placeholder={FORM.CONDITIONS.PROFILE.PLACEHOLDER}
                          {...bindArrayInputProps({
                            parent: "params",
                            index: i,
                            name: `rules.${INSURANCE_PLAN_PARAMS_RULES.PROFILES}.value`,
                            values,
                            ...formProps,
                          })}
                          value={
                            get(
                              values,
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROFILES}.value`,
                            ) || []
                          }
                          onChange={(value) =>
                            setFieldValue(
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROFILES}.value`,
                              value,
                            )
                          }
                        />
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.SUB_PROFILE.LABEL}</Col>
                      <Col sm={20} lg={20}>
                        <RuleMultiSelectWithExclude
                          paramIndex={i}
                          ruleName={INSURANCE_PLAN_PARAMS_RULES.SUB_PROFILES}
                          excludeLabel={FORM.CONDITIONS.EXCLUDED}
                          excludeFlags={excludeFlags}
                          setExcludeFlags={setExcludeFlags}
                          allOptionValues={Object.values(
                            LMF_ASSOCIATION_MEMBER_TYPE,
                          )}
                          allOptionValuesRef={dynamicAllValuesRef}
                          allowClear
                          mode="multiple"
                          options={Object.entries(LMF_ASSOCIATION_MEMBER_TYPE)
                            .map(([key, value]) => ({
                              label: FORM.CONDITIONS.SUB_PROFILE.OPTIONS[key],
                              value,
                            }))
                            .sort((a, b) =>
                              get(a, "label", "").localeCompare(b.label),
                            )}
                          placeholder={FORM.CONDITIONS.SUB_PROFILE.PLACEHOLDER}
                          {...bindArrayInputProps({
                            parent: "params",
                            index: i,
                            name: `rules.${INSURANCE_PLAN_PARAMS_RULES.SUB_PROFILES}.value`,
                            values,
                            ...formProps,
                          })}
                          value={
                            get(
                              values,
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.SUB_PROFILES}.value`,
                            ) || []
                          }
                          onChange={(value) =>
                            setFieldValue(
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.SUB_PROFILES}.value`,
                              value,
                            )
                          }
                        />
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.DEPARTMENTS}</Col>
                      <Col sm={20} lg={20}>
                        <RuleMultiSelectWithExclude
                          paramIndex={i}
                          ruleName={INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS}
                          excludeLabel={FORM.CONDITIONS.EXCLUDED}
                          excludeFlags={excludeFlags}
                          setExcludeFlags={setExcludeFlags}
                          allOptionValues={DEPARTMENTS}
                          allOptionValuesRef={dynamicAllValuesRef}
                          mode="multiple"
                          options={DEPARTMENTS.map((value) => ({
                            value,
                            label: value,
                          }))}
                          placeholder={FORM.CONDITIONS.DEPARTMENTS}
                          {...bindArrayInputProps({
                            parent: "params",
                            index: i,
                            name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS}.value`,
                            values,
                            ...formProps,
                          })}
                          value={
                            get(
                              values,
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS}.value`,
                            ) || []
                          }
                          onChange={(value) =>
                            setFieldValue(
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS}.value`,
                              value,
                            )
                          }
                        />
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.ZIP_CODES}</Col>
                      <Col sm={20} lg={20}>
                        <Select
                          mode="tags"
                          maxTagCount="responsive"
                          placeholder={FORM.CONDITIONS.ZIP_CODES}
                          {...bindArrayInputProps({
                            parent: "params",
                            index: i,
                            name: `rules.${INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES}.value`,
                            values,
                            ...formProps,
                          })}
                          value={
                            get(
                              values,
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES}.value`,
                            ) || []
                          }
                          onChange={(value) =>
                            setFieldValue(
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES}.value`,
                              value,
                            )
                          }
                        />
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.IDCC.LABEL}</Col>
                      <Col sm={20} lg={20}>
                        <InsurancePlanIdccRuleSelect
                          placeholder={FORM.CONDITIONS.IDCC.PLACEHOLDER}
                          value={
                            get(
                              values,
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.IDCC}.value`,
                            ) || []
                          }
                          onChange={(next) =>
                            setFieldValue(
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.IDCC}.value`,
                              next,
                            )
                          }
                        />
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.PROCESSES}</Col>
                      <Col span={20}>
                        <Processes
                          NoData={() => <></>}
                          View={({ data }) => (
                            <Row gutter={[16, 16]} align={"middle"}>
                              <Col span={24}>
                                <RuleMultiSelectWithExclude
                                  paramIndex={i}
                                  ruleName={
                                    INSURANCE_PLAN_PARAMS_RULES.PROCESSES
                                  }
                                  excludeLabel={FORM.CONDITIONS.EXCLUDED}
                                  excludeFlags={excludeFlags}
                                  setExcludeFlags={setExcludeFlags}
                                  allOptionValues={data?.map(({ id }) => id)}
                                  allOptionValuesRef={dynamicAllValuesRef}
                                  mode="multiple"
                                  options={data?.map(({ id, name }) => ({
                                    value: id,
                                    label: name,
                                  }))}
                                  placeholder={FORM.CONDITIONS.PROCESSES}
                                  {...bindArrayInputProps({
                                    parent: "params",
                                    index: i,
                                    name: `rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
                                    values,
                                    ...formProps,
                                  })}
                                  value={
                                    get(
                                      values,
                                      `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
                                    ) || []
                                  }
                                  onChange={(value) =>
                                    setFieldValue(
                                      `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
                                      value,
                                    )
                                  }
                                />
                              </Col>
                              <Col span={24}>
                                <Row gutter={[16, 16]} align={"middle"}>
                                  {get(
                                    values,
                                    `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
                                    [],
                                  ).map((process, j) => (
                                    <>
                                      <Col span={24}>
                                        <h3>
                                          {get(
                                            find(
                                              data,
                                              ({ id }) => id === process,
                                            ),
                                            "name",
                                          )}
                                        </h3>
                                      </Col>
                                      <Col span={24}>
                                        <Row gutter={[16, 16]}>
                                          <FieldArray
                                            key={`process-params${process}`}
                                            name={`params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents`}
                                            render={(arrayHelpers) => [
                                              get(
                                                values,
                                                `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents`,
                                                [],
                                              ).map((_, index) => (
                                                <>
                                                  <Col span={7}></Col>
                                                  <Col span={8}>
                                                    <Select
                                                      options={GENERATED_DOCUMENTS_TYPES.map(
                                                        (key) => ({
                                                          label:
                                                            CREATE.DOCUMENTS[
                                                              key
                                                            ].TITLE,
                                                          value: key,
                                                        }),
                                                      )}
                                                      placeholder={
                                                        CREATE.DOCUMENTS
                                                          .PLACEHOLDER
                                                      }
                                                      {...bindArrayInputProps({
                                                        parent: `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents`,
                                                        index,
                                                        name: `documentName`,
                                                        values,
                                                        ...formProps,
                                                      })}
                                                    />
                                                  </Col>
                                                  <Col span={8}>
                                                    <Select
                                                      options={GENERATED_DOCUMENTS_VERSIONS[
                                                        get(
                                                          values,
                                                          `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents.${index}.documentName`,
                                                        )
                                                      ].map((key) => ({
                                                        label:
                                                          CREATE.DOCUMENTS[
                                                            get(
                                                              values,
                                                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents.${index}.documentName`,
                                                            )
                                                          ]?.TEMPLATES[key],
                                                        value: key,
                                                      }))}
                                                      placeholder={
                                                        CREATE.DOCUMENTS
                                                          .PLACEHOLDER
                                                      }
                                                      {...bindArrayInputProps({
                                                        parent: `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents`,
                                                        index,
                                                        name: `documentVersion`,
                                                        values,
                                                        ...formProps,
                                                      })}
                                                    />
                                                  </Col>
                                                  <Col span={1}>
                                                    <BiTrash
                                                      color={COLORS.C_DANGER}
                                                      size={24}
                                                      onClick={() => {
                                                        arrayHelpers.remove(i);
                                                      }}
                                                    />
                                                  </Col>
                                                </>
                                              )),
                                              <Col
                                                key="add-offer-row"
                                                span={24}
                                              >
                                                <Button
                                                  type="primary"
                                                  onClick={() => {
                                                    arrayHelpers.push({
                                                      process,
                                                    });
                                                  }}
                                                >
                                                  <PlusOutlined
                                                    color={COLORS.C_WHITE}
                                                  />
                                                </Button>
                                              </Col>,
                                            ]}
                                          />
                                        </Row>
                                      </Col>
                                    </>
                                  ))}
                                </Row>
                              </Col>
                            </Row>
                          )}
                        />
                      </Col>
                    </Row>
                  </div>
                </Col>,
              ]),
              <div className="m-top-16" key="divider" />,
              <Button
                type="primary"
                key="add-row"
                onClick={() => {
                  arrayHelpers.push({
                    id: cuid(),
                    rules: {},
                  });
                }}
              >
                <PlusOutlined color={COLORS.C_WHITE} />
              </Button>,
            ]}
          />
        </Card>,
        <div key="margin-4" className="m-bottom-16" />,
        <Card
          title={FORM.EXTERNAL_LINKS.TITLE}
          divider={false}
          key="external_links"
        >
          <FieldArray
            name={"fields.externalLinks"}
            render={(arrayHelpers) => (
              <div className="grid--1">
                {get(values, "fields.externalLinks", []).map((_, i) => (
                  <div className="d-flex" key={`externalLinks-${i}`}>
                    <div className="flex--grow-1 grid--3">
                      <Input
                        label={FORM.EXTERNAL_LINKS.TEXT}
                        placeholder={FORM.EXTERNAL_LINKS.TEXT}
                        {...bindArrayInputProps({
                          parent: "fields.externalLinks",
                          index: i,
                          name: `text`,
                          values,
                          ...formProps,
                        })}
                      />
                      <Input
                        label={FORM.EXTERNAL_LINKS.LINK}
                        placeholder={FORM.EXTERNAL_LINKS.LINK}
                        {...bindArrayInputProps({
                          parent: "fields.externalLinks",
                          index: i,
                          name: `value`,
                          values,
                          ...formProps,
                        })}
                      />
                    </div>
                    <BiTrash
                      color={COLORS.C_DANGER}
                      size={24}
                      onClick={() => {
                        arrayHelpers.remove(i);
                      }}
                    />
                  </div>
                ))}

                <Button
                  type="primary"
                  key="add-row"
                  onClick={() => {
                    arrayHelpers.push({});
                  }}
                >
                  <PlusOutlined color={COLORS.C_WHITE} />
                </Button>
              </div>
            )}
          />
        </Card>,
        <div key="margin-flagship" className="m-bottom-16" />,
        <Card
          title={FORM.FLAGSHIP_GUARANTEES.TITLE}
          divider={false}
          key="additional_info"
        >
          <FieldArray
            name={"fields.flagshipGuarantees"}
            render={(arrayHelpers) => (
              <div className="grid--1">
                {get(values, "fields.flagshipGuarantees", []).map((_, i) => (
                  <div key={`flagshipGuaranties-${i}`}>
                    <div className="d-flex justify--end m-bottom-8">
                      <BiTrash
                        color={COLORS.C_DANGER}
                        size={24}
                        onClick={() => {
                          arrayHelpers.remove(i);
                        }}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                    <div className="grid--3 m-bottom-16">
                      <Input
                        label={FORM.FLAGSHIP_GUARANTEES.NAME}
                        placeholder={FORM.FLAGSHIP_GUARANTEES.NAME}
                        {...bindArrayInputProps({
                          parent: "fields.flagshipGuarantees",
                          index: i,
                          name: `coverage`,
                          values,
                          ...formProps,
                        })}
                      />
                      <Input
                        label={FORM.FLAGSHIP_GUARANTEES.VALUE}
                        placeholder={FORM.FLAGSHIP_GUARANTEES.VALUE}
                        {...bindArrayInputProps({
                          parent: "fields.flagshipGuarantees",
                          index: i,
                          name: `limit`,
                          values,
                          ...formProps,
                        })}
                      />
                      <Input
                        label={FORM.FLAGSHIP_GUARANTEES.ANNEX}
                        placeholder={FORM.FLAGSHIP_GUARANTEES.ANNEX}
                        {...bindArrayInputProps({
                          parent: "fields.flagshipGuarantees",
                          index: i,
                          name: `complement`,
                          values,
                          ...formProps,
                        })}
                      />
                    </div>
                    <div className="divider m-top-16 m-bottom-16" />
                  </div>
                ))}

                <Button
                  type="primary"
                  key="add-row"
                  onClick={() => {
                    arrayHelpers.push({});
                  }}
                >
                  <PlusOutlined color={COLORS.C_WHITE} />
                </Button>
              </div>
            )}
          />
        </Card>,
        <div key="margin-headlight-warranty" className="m-bottom-16" />,
        <Card
          title={FORM.HEADLIGHT_WARRANTY.TITLE}
          divider={false}
          key="headlight_warranty"
        >
          <FieldArray
            name={"supportingInformation"}
            render={(arrayHelpers) => {
              const headlightWarrantyIndex = get(
                values,
                "supportingInformation",
                [],
              ).findIndex(
                (item) =>
                  item?.category ===
                  SUPPORTING_INFORMATION_CATEGORIES.HEADLIGHT_WARRANTY,
              );

              const headlightWarranty =
                headlightWarrantyIndex >= 0
                  ? get(
                      values,
                      `supportingInformation.${headlightWarrantyIndex}`,
                    )
                  : null;

              const guaranteeValue = headlightWarranty?.value || [];

              return (
                <div className="grid--1">
                  {headlightWarrantyIndex < 0 && (
                    <Button
                      type="primary"
                      onClick={() => {
                        arrayHelpers.push({
                          id: cuid(),
                          category:
                            SUPPORTING_INFORMATION_CATEGORIES.HEADLIGHT_WARRANTY,
                          value: [],
                        });
                      }}
                    >
                      <PlusOutlined color={COLORS.C_WHITE} />
                      {FORM.HEADLIGHT_WARRANTY.ADD_CONFIGURATION}
                    </Button>
                  )}

                  {headlightWarrantyIndex >= 0 && (
                    <FieldArray
                      name={`supportingInformation.${headlightWarrantyIndex}.value`}
                      render={(guaranteeArrayHelpers) => (
                        <div>
                          {guaranteeValue.map((_, j) => (
                            <div key={`guarantee-${j}`}>
                              <div className="d-flex justify--end m-bottom-8">
                                <BiTrash
                                  color={COLORS.C_DANGER}
                                  size={24}
                                  onClick={() => {
                                    guaranteeArrayHelpers.remove(j);
                                  }}
                                  style={{ cursor: "pointer" }}
                                />
                              </div>
                              <div className="m-bottom-16">
                                <Input
                                  label={
                                    FORM.HEADLIGHT_WARRANTY.MAIN_INFO.LABEL
                                  }
                                  placeholder={
                                    FORM.HEADLIGHT_WARRANTY.MAIN_INFO
                                      .PLACEHOLDER
                                  }
                                  {...bindArrayInputProps({
                                    parent: `supportingInformation.${headlightWarrantyIndex}.value`,
                                    index: j,
                                    name: `mainInfo`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </div>
                              <div className="m-bottom-16">
                                <Input
                                  type="textarea"
                                  label={
                                    FORM.HEADLIGHT_WARRANTY.DESCRIPTION.LABEL
                                  }
                                  placeholder={
                                    FORM.HEADLIGHT_WARRANTY.DESCRIPTION
                                      .PLACEHOLDER
                                  }
                                  autoSize={{ minRows: 2, maxRows: 4 }}
                                  {...bindArrayInputProps({
                                    parent: `supportingInformation.${headlightWarrantyIndex}.value`,
                                    index: j,
                                    name: `description`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </div>
                              <div className="m-bottom-16">
                                <Input
                                  label={FORM.HEADLIGHT_WARRANTY.EXAMPLE.LABEL}
                                  placeholder={
                                    FORM.HEADLIGHT_WARRANTY.EXAMPLE.PLACEHOLDER
                                  }
                                  {...bindArrayInputProps({
                                    parent: `supportingInformation.${headlightWarrantyIndex}.value`,
                                    index: j,
                                    name: `example`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </div>
                              <div className="grid--3 m-bottom-16">
                                <Input
                                  type="number"
                                  label={
                                    FORM.HEADLIGHT_WARRANTY.DETAILS
                                      .SOCIAL_SECURITY.LABEL
                                  }
                                  placeholder={
                                    FORM.HEADLIGHT_WARRANTY.DETAILS
                                      .SOCIAL_SECURITY.PLACEHOLDER
                                  }
                                  {...bindArrayInputProps({
                                    parent: `supportingInformation.${headlightWarrantyIndex}.value`,
                                    index: j,
                                    name: `details.socialSecurity`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                                <Input
                                  type="number"
                                  label={
                                    FORM.HEADLIGHT_WARRANTY.DETAILS.INSURANCE
                                      .LABEL
                                  }
                                  placeholder={
                                    FORM.HEADLIGHT_WARRANTY.DETAILS.INSURANCE
                                      .PLACEHOLDER
                                  }
                                  {...bindArrayInputProps({
                                    parent: `supportingInformation.${headlightWarrantyIndex}.value`,
                                    index: j,
                                    name: `details.insurance`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                                <Input
                                  type="number"
                                  label={
                                    FORM.HEADLIGHT_WARRANTY.DETAILS.REMAINING
                                      .LABEL
                                  }
                                  placeholder={
                                    FORM.HEADLIGHT_WARRANTY.DETAILS.REMAINING
                                      .PLACEHOLDER
                                  }
                                  {...bindArrayInputProps({
                                    parent: `supportingInformation.${headlightWarrantyIndex}.value`,
                                    index: j,
                                    name: `details.remaining`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </div>
                              <div className="divider m-top-16 m-bottom-16" />
                            </div>
                          ))}

                          <Button
                            type="primary"
                            onClick={() => {
                              guaranteeArrayHelpers.push({
                                mainInfo: "",
                                description: "",
                                example: "",
                                details: {
                                  socialSecurity: 0,
                                  insurance: 0,
                                  remaining: 0,
                                },
                              });
                            }}
                          >
                            <PlusOutlined color={COLORS.C_WHITE} />
                            {FORM.HEADLIGHT_WARRANTY.ADD_GUARANTEE}
                          </Button>
                        </div>
                      )}
                    />
                  )}
                </div>
              );
            }}
          />
        </Card>,
      ]}
    </Formik>
  );
};

export default Create;
